/**
 * useAnalyticsCategories Hook
 * 
 * Fetches categories and groups for Analytics page.
 * Handles loading state and error handling.
 */

import React, { useEffect, useState } from "react";
import { auth } from "../../../firebase";
import { api } from "../../../services/api";
import { TransactionCategory, TransactionCategoryGroup } from "../../../types";
import { isDemoMode } from "../../../utils/demoMode";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";

export interface UseAnalyticsCategoriesParams {
  workspaceId: string;
}

export interface UseAnalyticsCategoriesResult {
  categories: TransactionCategory[];
  groups: TransactionCategoryGroup[];
  loading: boolean;
  setCategories: React.Dispatch<React.SetStateAction<TransactionCategory[]>>;
  setGroups: React.Dispatch<React.SetStateAction<TransactionCategoryGroup[]>>;
}

// Cache for categories and groups per workspace - persists across component unmounts
const categoriesCache = new Map<string, {
  categories: TransactionCategory[];
  groups: TransactionCategoryGroup[];
  timestamp: number;
}>();

// Cache expiration time: 5 minutes
const CACHE_EXPIRY_MS = 5 * 60 * 1000;

/**
 * Hook for fetching categories and groups for Analytics
 * Uses caching to avoid reloading data when navigating back to Analytics
 */
export const useAnalyticsCategories = ({
  workspaceId,
}: UseAnalyticsCategoriesParams): UseAnalyticsCategoriesResult => {
  // Check cache first
  const cachedData = categoriesCache.get(workspaceId);
  const hasValidCache = !!(cachedData && (Date.now() - cachedData.timestamp) < CACHE_EXPIRY_MS);

  const [categories, setCategories] = useState<TransactionCategory[]>(
    hasValidCache && cachedData ? cachedData.categories : []
  );
  const [groups, setGroups] = useState<TransactionCategoryGroup[]>(
    hasValidCache && cachedData ? cachedData.groups : []
  );
  const [loading, setLoading] = useState<boolean>(!hasValidCache);

  useEffect(() => {
    if (!workspaceId || isDemoMode()) {
      setCategories([]);
      setGroups([]);
      setLoading(false);
      return;
    }

    // Don't fetch until user is authenticated (or bypass/demo) - avoids "User not authenticated" on initial load
    if (!auth.currentUser && !isBypassAuthEnabled()) {
      setLoading(false);
      return;
    }

    // If we have valid cache, skip fetching (but still set up listener if needed)
    if (hasValidCache) {
      setLoading(false);
      return;
    }

    const fetchCategoriesAndGroups = async (): Promise<void> => {
      setLoading(true);
      try {
        const expenseCategories = await api.getCategories(workspaceId, "expense");
        const incomeCategories = await api.getCategories(workspaceId, "income");
        const allCategories = [...expenseCategories, ...incomeCategories];

        const expenseGroups = await api.getGroups(workspaceId, "expense");
        const incomeGroups = await api.getGroups(workspaceId, "income");
        const allGroups = [...expenseGroups, ...incomeGroups];

        // Update cache
        categoriesCache.set(workspaceId, {
          categories: allCategories,
          groups: allGroups,
          timestamp: Date.now(),
        });

        setCategories(allCategories);
        setGroups(allGroups);
      } catch (err) {
        console.error("[useAnalyticsCategories] Error fetching categories and groups:", err);
        setCategories([]);
        setGroups([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCategoriesAndGroups();
  }, [workspaceId, hasValidCache]);

  return {
    categories,
    groups,
    loading,
    setCategories,
    setGroups,
  };
};
